import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { scrollToSection as scrollTo } from "@/lib/scroll";
import { ArrowRight, ShieldCheck, Star } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/34539243/pexels-photo-34539243.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 } }),
};

export default function Hero() {
  const { t } = useLang();
  const stats = [
    { v: t("hero.stat1"), l: t("hero.stat1_l") },
    { v: t("hero.stat2"), l: t("hero.stat2_l") },
    { v: t("hero.stat3"), l: t("hero.stat3_l") },
    { v: t("hero.stat4"), l: t("hero.stat4_l") },
  ];

  return (
    <section id="top" className="relative pt-[74px] bg-ksy-navy overflow-hidden" data-testid="hero-section">
      {/* subtle grid accents */}
      <div className="absolute inset-0 opacity-[0.06] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:56px_56px]" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center py-16 md:py-24">
        <div>
          <motion.span
            variants={fade} initial="hidden" animate="show" custom={0}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-blue-100"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> {t("hero.badge")}
          </motion.span>

          <motion.h1
            variants={fade} initial="hidden" animate="show" custom={1}
            className="mt-5 font-display font-extrabold text-white tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            {t("hero.title")}
          </motion.h1>

          <motion.p
            variants={fade} initial="hidden" animate="show" custom={2}
            className="mt-5 text-lg text-blue-100/80 max-w-xl leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => scrollTo("book")}
              className="group inline-flex items-center gap-2 bg-ksy-blue text-white font-bold px-6 py-3.5 rounded-lg hover:bg-blue-600 transition-colors shadow-lg shadow-blue-900/30"
              data-testid="hero-quote-cta"
            >
              {t("hero.cta")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => scrollTo("drivers")}
              className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-bold px-6 py-3.5 rounded-lg hover:bg-white/15 transition-colors"
              data-testid="hero-drivers-cta"
            >
              {t("hero.secondary")}
            </button>
          </motion.div>

          <motion.div variants={fade} initial="hidden" animate="show" custom={4} className="mt-6 flex items-center gap-2 text-blue-100/70 text-sm">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}</div>
            <span className="font-semibold">4.9/5</span> · 1,200+ shipments this year
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="relative"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
            <img src={HERO_IMG} alt="KS&Y car carrier truck loaded with vehicles" className="w-full h-[300px] md:h-[440px] object-cover" />
          </div>
          <div className="absolute -bottom-5 -left-3 md:left-6 bg-white rounded-xl shadow-xl px-5 py-4 flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-lg bg-green-100 text-green-600"><ShieldCheck className="w-5 h-5" /></span>
            <div>
              <div className="text-sm font-extrabold text-ksy-navy leading-tight">Fully insured</div>
              <div className="text-xs text-slate-500">USDOT & MC authority</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* stat bar */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {stats.map((s, i) => (
            <div key={i} className="py-6 md:py-7 px-2 text-center md:text-left" data-testid={`hero-stat-${i}`}>
              <div className="font-display font-extrabold text-2xl md:text-3xl text-white">{s.v}</div>
              <div className="text-xs md:text-sm text-blue-100/60 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
